import { z } from "zod";
import { TObjectBaseTDate } from "../base";
import { TCRUDByTDate } from "../common/CRUDBy";
import { TRelatives } from "../common/relative";
import { TReadAccess } from "../common/readAccess";
import { TDate } from "../common/date";

export const TProcessingState = z.enum([
  "unprocessed",
  "processing",
  "processed",
  "draft",
  "saved",
  "error",
]);
export type IProcessingState = z.infer<typeof TProcessingState>;

export const TDocumentAIClassification = z.object({
  confidence: z.number(),
  type: z.string(),
});

export const TFirebaseDataExternalReference = z.object({
  apiClientID: z.string().optional(),
  id: z.string(),
});

export const TExternalFileMetadata = z.object({
  propertyID: z.string().optional(),
});

export const TPropelyAIDeviation = z.object({
  id: z.string(),
  title: z.string().optional(),
  description: z.string().optional(),
  rejected: z.boolean(),
  priority: z.string().optional(),
  status: z.string().optional(),
  solvedDate: TDate.optional(),
  deadline: TDate.optional(),
  deviationID: z.string().optional(),
  propertyID: z.string().optional(),
});
export type IPropelyAIDeviation = z.infer<typeof TPropelyAIDeviation>;

// Define the shared attributes as a base schema
export const TFileBaseDataFields = z.object({
  originalCreatedAt: TDate.nullish(),
  drawingDate: TDate.nullish(),
  relatives: TRelatives.optional(),
  propertyIDs: z.array(z.string()).optional(),
  contractorID: z.string().nullish(),
  contractorName: z.string().optional(),
});
export type IFileBaseDataFields = z.infer<typeof TFileBaseDataFields>;

// Extend the base schema for the Service Report
export const TDataFieldsServiceReport = z.object({
  responsiblePerson: z.string().optional(),
  contractorID: z.string().nullish(),
  contractorName: z.string().optional(),
  executedDate: TDate.nullish(),
  deviations: z.array(TPropelyAIDeviation).optional(),
});
export type IDataFieldsServiceReport = z.infer<typeof TDataFieldsServiceReport>;

// Extend the base schema for the Service Agreement
export const TDataFieldsServiceAgreement = z.object({
  name: z.string().optional(),
  contractorID: z.string().nullish(),
  contractorName: z.string().optional(),
  agreementStartDate: TDate.optional(),
  agreementEndDate: TDate.optional(),
  agreementSignDate: TDate.optional(),
  annualReportCount: z.number().nullish(),
});
export type IDataFieldsServiceAgreement = z.infer<
  typeof TDataFieldsServiceAgreement
>;

export const TDataFieldsDrawing = z.object({
  contractorID: z.string().nullish(),
  contractorName: z.string().optional(),
  drawingTitle: z.string().optional(),
  drawingNumber: z.string().optional(),
  drawingDate: TDate.nullish(),
  discipline: z.string().optional(),
  ns3451Code: z.string().optional(),
  drawingCategory: z.string().optional(),
  revisionDate: TDate.nullish(),
  revision: z.string().optional(),
});
export type IDataFieldsDrawing = z.infer<typeof TDataFieldsDrawing>;

export const TFileDataFields = TFileBaseDataFields.and(TDataFieldsServiceReport)
  .and(TDataFieldsServiceAgreement)
  .and(TDataFieldsDrawing);

export type IFileDataFields = z.infer<typeof TFileDataFields>;

export const TDocumentAIType = z.enum([
  "agreement",
  "report",
  "drawing",
  "other",
]);
export type IDocumentAIType = z.infer<typeof TDocumentAIType>;

export const TRawFile = z
  .object({
    name: z.string(),
    type: z.string(),
    storagePath: z.string().optional(),
    previewPath: z.string().optional(),
    originalZipPath: z.string().optional(),
    taskPaths: z.array(z.string()).optional(),
    size: z.number().optional(),
    senderEmail: z.string().optional(),
    recipientEmail: z.string().optional(),
    emailText: z.string().optional(),
    isCorrupt: z.boolean().optional(),

    rawText: z.string().optional(),
    contentHash: z.string().optional(),

    externalURL: z.string().optional(),
    dataUrl: z.string().optional(),
    folderIDs: z.array(z.string()).optional(),

    fileURL: z.string().optional(),
    tags: z.array(z.string()).default([]),
    readAccess: TReadAccess.default({}),
    archived: z.boolean().default(false),
    relatives: TRelatives.optional(),
    parentFileID: z.string().optional(),

    externalFileClientID: z.string().optional(),
    externalRefs: z.record(TFirebaseDataExternalReference).optional(),
    externalFileMetadata: TExternalFileMetadata.optional(),

    previousAIProcessingTime: TDate.optional(),
    contentSummary: z.string().optional(),
    documentType: TDocumentAIType.nullish(),
    selectedClassification: TDocumentAIClassification.nullish(),
    classifications: z.array(TDocumentAIClassification).default([]),
    processingState: TProcessingState.default("unprocessed"),
    propertyIDs: z.array(z.string()).default([]),

    AIDataFields: TFileDataFields.optional(),
    dataFields: TFileDataFields.optional(),

    projectID: z.string().optional(),
  })
  .extend(TObjectBaseTDate.shape);
export type IRawFile = z.infer<typeof TRawFile>;

// Zip inheritance data
export const TZipInheritanceData = z.object({
  createdBy: TCRUDByTDate.optional(),
  readAccess: TReadAccess.default({}),
  senderEmail: z.string().optional(),
});
export type IZipInheritanceData = z.infer<typeof TZipInheritanceData>;
